import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { isRecurrencyEnabled } from '@cannect/utils/featureFlags'
import { QUERY_KEYS } from './constants'
import { cartLinkMode } from './helpers'
import {
  addSubscriptionItem,
  cancelSubscription,
  createFromOrder,
  getEligibility,
  getOrderRecurrencyAdhesion,
  getSubscription,
  getTerms,
  listRecurrencyCards,
  listSubscriptions,
  patchSubscription,
  pauseSubscription,
  postponeSubscription,
  previewFromOrder,
  previewSubscription,
  removeSubscriptionItem,
  resumeSubscription,
  skipSubscription,
  uploadSubscriptionDocument,
  type EligibilityParams
} from './requests'
import type {
  ApiEnvelope,
  CartRecurrencySummary,
  RecurrencyOffer,
  SubscriptionDetail,
  SubscriptionPreviewBody,
  SubscriptionStatus
} from './types'

// Visibilidade (data-model §1): flag de build + conta logada (paciente ou
// profissional, 010 R7). Nenhuma
// requisição de recorrência sai sem `enabled`. "Minhas assinaturas" só para
// quem já tem ou teve assinatura (qualquer status).
export const useRecurrencyAccess = () => {
  const { signedUser } = useSignedUser() || ({} as ReturnType<typeof useSignedUser>)
  // 010 (research R7, decisão de 04/11/2026): qualquer conta logada assina,
  // paciente ou profissional de saúde.
  const enabled = isRecurrencyEnabled() && Boolean(signedUser?.id)

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.access,
    queryFn: async () => {
      try {
        return (await listSubscriptions()).length > 0
      } catch {
        return false
      }
    },
    enabled,
    staleTime: 10 * 60_000
  })
  const hasSubscription = enabled && Boolean(data)

  return { enabled, hasSubscription, showNavigation: hasSubscription, isLoading: enabled && isLoading }
}

export const useEligibility = (params: EligibilityParams, { enabled = true } = {}) => {
  const { enabled: accessEnabled } = useRecurrencyAccess()
  const ids = params.productIds || []

  return useQuery({
    queryKey: QUERY_KEYS.eligibility(
      ids,
      params.quantities || [],
      params.cycleDays,
      Boolean(params.prices),
      params.origin
    ),
    queryFn: () => getEligibility(params),
    enabled: enabled && accessEnabled && ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false
  })
}

export const useSubscriptions = (status?: SubscriptionStatus) => {
  const { enabled } = useRecurrencyAccess()

  return useQuery({
    queryKey: QUERY_KEYS.subscriptions(status),
    queryFn: () => listSubscriptions(status),
    enabled
  })
}

export const useSubscription = (id?: number | null, { enabled = true } = {}) => {
  const { enabled: accessEnabled } = useRecurrencyAccess()

  return useQuery({
    queryKey: QUERY_KEYS.subscription(Number(id)),
    queryFn: () => getSubscription(Number(id)),
    enabled: enabled && accessEnabled && Boolean(id)
  })
}

export const useRecurrencyTerms = () => {
  const { enabled } = useRecurrencyAccess()

  return useQuery({ queryKey: QUERY_KEYS.terms, queryFn: getTerms, enabled, staleTime: 30 * 60_000 })
}

export const useRecurrencyCards = ({ enabled = true } = {}) => {
  const { enabled: accessEnabled } = useRecurrencyAccess()

  return useQuery({ queryKey: QUERY_KEYS.cards, queryFn: listRecurrencyCards, enabled: enabled && accessEnabled })
}

export const useOrderRecurrencyAdhesion = (orderId?: number | null, { enabled = true } = {}) => {
  const { enabled: accessEnabled } = useRecurrencyAccess()

  return useQuery({
    queryKey: QUERY_KEYS.adhesion(Number(orderId)),
    queryFn: () => getOrderRecurrencyAdhesion(Number(orderId)),
    enabled: enabled && accessEnabled && Boolean(orderId),
    retry: false
  })
}

const hasPreviewBody = (body?: SubscriptionPreviewBody | null) =>
  Boolean(
    body &&
    ((body.items && body.items.length) ||
      body.cycleDays !== undefined ||
      body.addItem ||
      body.removeProductId !== undefined)
  )

// Prévias (deltas B8/B9): só leitura, nada é invalidado.
export const useSubscriptionPreview = (id: number, body?: SubscriptionPreviewBody | null) => {
  const { enabled } = useRecurrencyAccess()

  return useQuery({
    queryKey: QUERY_KEYS.preview(id, body),
    queryFn: () => previewSubscription(id, body as SubscriptionPreviewBody),
    enabled: enabled && Boolean(id) && hasPreviewBody(body),
    placeholderData: keepPreviousData,
    retry: false
  })
}

export const useAdhesionPreview = (orderId: number, cycleDays?: number | null, { enabled = true } = {}) => {
  const { enabled: accessEnabled } = useRecurrencyAccess()

  return useQuery({
    queryKey: QUERY_KEYS.adhesionPreview(orderId, cycleDays),
    queryFn: () => previewFromOrder(orderId, { cycleDays: cycleDays ?? undefined }),
    enabled: enabled && accessEnabled && Boolean(orderId) && Boolean(cycleDays),
    placeholderData: keepPreviousData,
    retry: false
  })
}

const useInvalidateSubscription = () => {
  const client = useQueryClient()

  // A resposta das mutations traz o detalhe atualizado: entra no cache na hora
  // (research R9) e as listas são refeitas em seguida.
  return (id?: number, response?: Partial<ApiEnvelope<SubscriptionDetail>> | null) => {
    if (id && response?.data && typeof response.data === 'object') {
      client.setQueryData(QUERY_KEYS.subscription(id), response.data)
    }
    client.invalidateQueries({ queryKey: ['recurrency', 'subscriptions'] })
    if (id) client.invalidateQueries({ queryKey: QUERY_KEYS.subscription(id) })
    client.invalidateQueries({ queryKey: QUERY_KEYS.cards })
  }
}

export const usePatchSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (body: Parameters<typeof patchSubscription>[1]) => patchSubscription(id, body),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const useAddSubscriptionItem = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (body: { productId: number; quantity: number }) => addSubscriptionItem(id, body),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const useRemoveSubscriptionItem = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (productId: number) => removeSubscriptionItem(id, productId),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const useSkipSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({ mutationFn: () => skipSubscription(id), onSuccess: (response) => invalidate(id, response) })
}

export const usePostponeSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (body: { nextChargeAt: string }) => postponeSubscription(id, body),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const usePauseSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (body: { until: string }) => pauseSubscription(id, body),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const useResumeSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({ mutationFn: () => resumeSubscription(id), onSuccess: (response) => invalidate(id, response) })
}

export const useCancelSubscription = (id: number) => {
  const invalidate = useInvalidateSubscription()
  return useMutation({
    mutationFn: (body: { reason?: string }) => cancelSubscription(id, body),
    onSuccess: (response) => invalidate(id, response)
  })
}

export const useCreateFromOrder = (orderId: number) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { cycleDays: number; cardId: number; termsVersion: string }) => createFromOrder(orderId, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['recurrency', 'subscriptions'] })
      client.invalidateQueries({ queryKey: QUERY_KEYS.adhesion(orderId) })
    }
  })
}

export const useUploadSubscriptionDocument = (id: number) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: uploadSubscriptionDocument,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['recurrency', 'subscriptions'] })
      client.invalidateQueries({ queryKey: QUERY_KEYS.subscription(id) })
    }
  })
}

// 010: estado do bloco de assinatura no carrinho do link do painel
// (data-model §3). Não usa `useRecurrencyAccess`, que exige sessão: o visitante
// anônimo também vê a oferta. O status do claim vem do `usePrepareCart`.
export const useCartLinkRecurrency = () => {
  const { lastOrderCreated, cartLinkClaimStatus, cartLinkLoginRequired } = usePrepareCart()
  const { signedUser } = useSignedUser() || ({} as ReturnType<typeof useSignedUser>)
  const offer: RecurrencyOffer | null = lastOrderCreated?.recurrencyOffer ?? null
  const summary: CartRecurrencySummary | null = lastOrderCreated?.recurrencySummary ?? null
  const signedIn = Boolean(signedUser?.id)
  const { mode, blockedReason } = cartLinkMode({
    featureEnabled: isRecurrencyEnabled(),
    offer,
    summary,
    signedIn,
    claimStatus: cartLinkClaimStatus
  })

  return {
    // Sessão caída num PUT com ciclo (401 RECURRENCY_LOGIN_REQUIRED): volta a
    // pedir login mesmo que a última oferta diga que o pedido é do usuário.
    mode: cartLinkLoginRequired && !signedIn && mode === 'owner' ? ('login_required' as const) : mode,
    blockedReason,
    offer,
    summary,
    signedIn,
    claimStatus: cartLinkClaimStatus
  }
}
